import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Settings, 
  Bell, 
  Palette, 
  Globe, 
  Download,
  Trash2,
  RefreshCw,
  Shield,
  Info
} from 'lucide-react';

import { useAppStore } from '../stores/appStore';
import { usePWA, useNotifications } from '../hooks/usePWA';

const SettingsPage = () => {
  const { t } = useTranslation();
  const { theme, setTheme } = useAppStore();
  const { isInstallable, installPWA, updateAvailable, updatePWA } = usePWA();
  const { isSupported, permission, sendTestNotification } = useNotifications();

  const settingsGroups = [
    {
      title: 'Apariencia',
      icon: Palette,
      settings: [
        {
          title: 'Tema',
          description: 'Personaliza el aspecto visual',
          type: 'select',
          value: theme,
          options: [
            { value: 'manga', label: 'Manga/Anime' },
            { value: 'corporate', label: 'Corporativo' },
            { value: 'festival', label: 'Festival' },
            { value: 'academic', label: 'Académico' },
          ],
          onChange: (value: string) => setTheme(value as any),
        },
      ],
    },
    {
      title: 'Notificaciones',
      icon: Bell,
      settings: [
        {
          title: 'Notificaciones push',
          description: `Estado: ${permission === 'granted' ? 'Activadas' : permission === 'denied' ? 'Bloqueadas' : 'Sin configurar'}`,
          type: 'action',
          action: sendTestNotification,
          actionLabel: 'Probar notificación',
          disabled: !isSupported || permission !== 'granted',
        },
        {
          title: 'Recordatorios',
          description: 'Minutos antes del evento',
          type: 'select',
          value: '5',
          options: [
            { value: '5', label: '5 minutos' },
            { value: '10', label: '10 minutos' },
            { value: '15', label: '15 minutos' },
            { value: '30', label: '30 minutos' },
          ],
          onChange: () => {},
        },
      ],
    },
    {
      title: 'Idioma',
      icon: Globe,
      settings: [
        {
          title: 'Idioma de la aplicación',
          description: 'Cambia el idioma de la interfaz',
          type: 'select',
          value: 'es',
          options: [
            { value: 'es', label: 'Español' },
            { value: 'en', label: 'English' },
            { value: 'pt', label: 'Português' },
            { value: 'fr', label: 'Français' },
          ],
          onChange: () => {},
        },
      ],
    },
    {
      title: 'Aplicación',
      icon: Settings,
      settings: [
        {
          title: 'Instalar aplicación',
          description: 'Instala PRO-Act en tu dispositivo',
          type: 'action',
          action: installPWA,
          actionLabel: 'Instalar',
          disabled: !isInstallable,
          hidden: !isInstallable,
        },
        {
          title: 'Actualizar aplicación',
          description: 'Nueva versión disponible',
          type: 'action',
          action: updatePWA,
          actionLabel: 'Actualizar',
          disabled: !updateAvailable,
          hidden: !updateAvailable,
        },
        {
          title: 'Limpiar cache',
          description: 'Elimina datos temporales',
          type: 'action',
          action: () => {
            if ('caches' in window) {
              caches.keys().then(names => {
                names.forEach(name => caches.delete(name));
              });
            }
          },
          actionLabel: 'Limpiar',
        },
      ],
    },
    {
      title: 'Información',
      icon: Info,
      settings: [
        {
          title: 'Versión',
          description: '1.0.0',
          type: 'info',
        },
        {
          title: 'Términos y condiciones',
          description: 'Lee nuestros términos de servicio',
          type: 'link',
          link: '/terms',
        },
        {
          title: 'Política de privacidad',
          description: 'Consulta cómo manejamos tus datos',
          type: 'link',
          link: '/privacy',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <motion.h1
            className="text-2xl font-bold text-gray-800 flex items-center space-x-3"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Settings className="w-7 h-7 text-primary-500" />
            <span>{t('navigation.settings')}</span>
          </motion.h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {settingsGroups.map((group, groupIndex) => (
            <motion.div
              key={group.title}
              className="bg-white rounded-2xl shadow-soft overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: groupIndex * 0.1 }}
            >
              {/* Group Header */}
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                  <group.icon className="w-5 h-5 text-primary-500" />
                  <span>{group.title}</span>
                </h2>
              </div>

              {/* Settings */}
              <div className="divide-y divide-gray-100">
                {group.settings
                  .filter(setting => !setting.hidden)
                  .map((setting, settingIndex) => (
                    <div key={settingIndex} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-800">
                            {setting.title}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {setting.description}
                          </p>
                        </div>

                        <div className="ml-4">
                          {setting.type === 'select' && (
                            <select
                              value={setting.value}
                              onChange={(e) => setting.onChange?.(e.target.value)}
                              className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                              {setting.options?.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          )}

                          {setting.type === 'toggle' && (
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input 
                                type="checkbox" 
                                className="sr-only peer" 
                                defaultChecked={setting.value as boolean}
                                onChange={(e) => setting.onChange?.(e.target.checked)}
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                            </label>
                          )}

                          {setting.type === 'action' && (
                            <motion.button
                              onClick={setting.action}
                              disabled={setting.disabled}
                              className="bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:cursor-not-allowed"
                              whileHover={!setting.disabled ? { scale: 1.05 } : {}}
                              whileTap={!setting.disabled ? { scale: 0.95 } : {}}
                            >
                              {setting.actionLabel}
                            </motion.button>
                          )}

                          {setting.type === 'link' && (
                            <button
                              onClick={() => window.open(setting.link, '_blank')}
                              className="text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors"
                            >
                              Ver más →
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <motion.div
          className="text-center py-8 text-sm text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <p>PRO-Act v1.0.0 - Desarrollado por Siete.Online</p>
          <p className="mt-2">
            Con ❤️ para la comunidad de eventos
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SettingsPage;